import React, { useCallback, useState } from "react";
import Map, { Source, Layer, Popup } from "react-map-gl/mapbox";
import locations from "../locations.json";
import { MAPBOX_ACCESS_TOKEN, MAPBOX_STYLES } from "config/mapConfig";

export default function MapboxGeoJSONMap({
  selectedLocations,
  onLocationSelect,
}) {
  const [hoverInfo, setHoverInfo] = useState(null);

  const onHover = useCallback((event) => {
    const feature = event.features && event.features[0];
    setHoverInfo(
      feature
        ? {
            lngLat: event.lngLat,
            name: feature.properties.NIL,
            feature: feature,
          }
        : null
    );
  }, []);

  const onClick = useCallback((event) => {
    const feature = event.features && event.features[0];
    if (feature) {
      onLocationSelect(feature);
    }
  }, []);

  // Convert the GeoJSON data to a format Mapbox can use
  const geojsonData = {
    type: "FeatureCollection",
    features: locations.features,
  };

  // Define the layer styles for unselected and selected features
  const unselectedLayerStyle = {
    id: "data-layer",
    type: "fill",
    paint: {
      "fill-color": "#0074c1",
      "fill-opacity": 0.1,
      "fill-outline-color": "#0074c1",
    },
  };

  const selectedLayerStyle = {
    id: "selected-layer",
    type: "fill",
    source: "selected-locations",
    paint: {
      "fill-color": "#0074c1",
      "fill-opacity": 0.8,
      "fill-outline-color": "#0074c1",
    },
  };

  const hoverLayerStyle = {
    id: "hover-layer",
    type: "fill",
    source: "hover-locations",
    paint: {
      "fill-color": "#0074c1",
      "fill-opacity": 0.8,
      "fill-outline-color": "#0074c1",
    },
  };

  const outlineLayerStyle = {
    id: "outline-layer",
    type: "line",
    paint: {
      "line-color": "#0074c1",
      "line-width": 2.5,
    },
  };

  // Create a GeoJSON source for selected locations
  const selectedFeaturesCollection = {
    type: "FeatureCollection",
    features: selectedLocations,
  };

  // Create a GeoJSON source for hover locations
  const hoverFeatureCollection = {
    type: "FeatureCollection",
    features: hoverInfo ? [hoverInfo.feature] : [],
  };

  return (
    <Map
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      initialViewState={{
        longitude: 9.1824,
        latitude: 45.4685,
        zoom: 11,
      }}
      style={{ height: "100vh", width: "100%" }}
      mapStyle={MAPBOX_STYLES.STREETS}
      scrollZoom={false}
      attributionControl={false}
      interactiveLayerIds={["data-layer"]}
      onClick={onClick}
      onMouseMove={onHover}
      doubleClickZoom={false}
    >
      <Source id="locations-data" type="geojson" data={geojsonData}>
        <Layer {...outlineLayerStyle} />
        <Layer {...unselectedLayerStyle} />
      </Source>

      {selectedLocations.length > 0 && (
        <Source
          id="selected-locations"
          type="geojson"
          data={selectedFeaturesCollection}
        >
          <Layer {...selectedLayerStyle} />
        </Source>
      )}

      {hoverInfo && (
        <>
          <Popup
            longitude={hoverInfo.lngLat.lng}
            latitude={hoverInfo.lngLat.lat}
            closeButton={false}
            closeOnClick={false}
            offset={[0, -10]}
          >
            <div style={{ fontSize: "12px" }}>{hoverInfo.name}</div>
          </Popup>
          <Source
            id="hover-locations"
            type="geojson"
            data={hoverFeatureCollection}
          >
            <Layer {...hoverLayerStyle} />
          </Source>
        </>
      )}
    </Map>
  );
}
